import MainLayout from "../../../layouts/MainLayout";
import "../../../assets/styles/custom-styles.css";
import Form from "../../../components/forms/Form";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import useCreateMovement from "./hooks/useCreateMovement";
import { normalizeFormErrors } from "../../../utils/objectUtils";
import { useMemo, useState } from "react";
import { useVolume } from "./hooks/useVolume";
import { Volume } from "./components/volumes/Volume";
import { Biped } from "./components/biped/Biped";
import ReservedEquipmentConfirmDialog from "./components/ReservedEquipmentConfirmDialog";

dayjs.extend(customParseFormat);

const CreateMovementPage = () => {
  const {
    formData,
    handleChange,
    handleSubmit,
    handleBack,
    hasMovementDraft,
    formErrors,
    createMovementMutation,
    viewTable,
    setViewTable,
    getOptions,
    getSelectedValue,
    formFields,
    addFieldsInData,
    equipments,
    products,
    setProducts,
    volumes,
    setVolumes,
    assets, // inputs dos equipamentos
    inputs, // inputs dos produtos
    control,
    setValue,
    isLoading,
    isDebouncePending,
    debouncedAddEquipment,
    flushAddEquipment,
    handleRemoveItem,
    handleRemoveBipedItem,
    firstInputRef,
    inputRefs,
    debouncedAddProduct,
    reservedConfirmState,
    handleReservedConfirm,
    handleReservedCancel,
    serviceOrderItemsData,
  } = useCreateMovement();
  const [exitDialogOpen, setExitDialogOpen] = useState(false);

  const requestBack = () => {
    if (!hasMovementDraft) {
      handleBack();
      return;
    }
    setExitDialogOpen(true);
  };

  const columns = [
    "Item da Requisição",
    "Quantidade Requisitada",
    "Tombo/Código",
    "Nome",
    "Ações",
  ];

  const data = useMemo(
    () => [...equipments, ...products],
    [equipments, products],
  );

  // * Volumes
  const assetsDataState = useMemo(
    () => ({
      // * Equipamentos Bipados
      ...assets,
      // * Insumos Bipados
      ...inputs,
    }),
    [assets, inputs],
  );

  const {
    newVolume,
    setNewVolume,
    openVolumesModal,
    handleOpenVolumesModal,
    handleCloseVolumesModal,
    handleRemoveVolume,
    handleEditVolume,
    getTotalAlreadyAllocated,
  } = useVolume({
    data,
    setVolumes,
    setProducts,
    volumes,
    assetsData: assetsDataState,
  });

  return (
    <MainLayout selectedCompany="ALUCOM">
      <ReservedEquipmentConfirmDialog
        open={reservedConfirmState.open}
        equipmentNumber={reservedConfirmState.equipmentNumber}
        customerAlias={reservedConfirmState.customerAlias}
        isBlocked={reservedConfirmState.isBlocked}
        blockedStatus={reservedConfirmState.blockedStatus}
        onConfirm={handleReservedConfirm}
        onCancel={handleReservedCancel}
      />
      <Dialog open={exitDialogOpen} onClose={() => setExitDialogOpen(false)}>
        <DialogTitle>Sair do cadastro?</DialogTitle>
        <DialogContent>
          Ao sair, o rascunho será descartado e as reservas atuais serão liberadas.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExitDialogOpen(false)}>Permanecer</Button>
          <Button color="primary" onClick={handleBack}>Sair</Button>
        </DialogActions>
      </Dialog>
      <PageHeader
        title="Cadastro de Movimento"
        showBackButton={true}
        backUrl="/aluguel/movimentos"
        onBack={requestBack}
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          onBack={requestBack}
          disabledSubmitButton={
            createMovementMutation.isPending || volumes.length === 0
          }
          isLoading={createMovementMutation.isPending}>
          {() => {
            const renderedSections = new Set();
            return formFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={viewTable}
                      setViewTable={setViewTable}
                      addFieldsInData={addFieldsInData}
                    />
                  )}
                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />

                  {formData?.movement?.service_order_id && (
                    <Box sx={{ my: 4, gap: 2 }}>
                      {/*🔹 Volumes */}
                      <Volume
                        bipeds={assetsDataState}
                        volumes={volumes}
                        setVolumes={setVolumes}
                        newVolume={newVolume}
                        setNewVolume={setNewVolume}
                        openVolumesModal={openVolumesModal}
                        handleOpenVolumesModal={handleOpenVolumesModal}
                        handleCloseVolumesModal={handleCloseVolumesModal}
                        handleRemoveVolume={handleRemoveVolume}
                        handleRemoveItem={handleRemoveItem}
                        handleEditVolume={handleEditVolume}
                      />

                      <Biped
                        data={data}
                        columns={columns}
                        assets={assets}
                        inputs={inputs}
                        volumes={volumes}
                        getTotalAlreadyAllocated={getTotalAlreadyAllocated}
                        control={control}
                        firstInputRef={firstInputRef}
                        inputRefs={inputRefs}
                        isLoading={isLoading}
                        isDebouncePending={isDebouncePending}
                        handleRemoveItem={handleRemoveItem}
                        debouncedAddEquipment={debouncedAddEquipment}
                        flushAddEquipment={flushAddEquipment}
                        debouncedAddProduct={debouncedAddProduct}
                        handleRemoveBipedItem={handleRemoveBipedItem}
                        // * Modal de Busca do produto
                        assetsData={assetsDataState}
                        setProducts={setProducts}
                        setValue={setValue}
                        serviceOrderId={
                          formData?.movement?.service_order_id?.value ||
                          formData?.movement?.service_order_id
                        }
                        serviceOrderItemsData={serviceOrderItemsData}
                      />
                    </Box>
                  )}
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateMovementPage;
